import { approximately, clamp01, EPSILON } from './MathUtils';

/**
 * A two dimensional point or direction in world units.
 *
 * Mutable by design. Transform maths runs for every entity every frame, and returning a fresh vector
 * from each operation is the easiest way to make a game loop stutter. Operations mutate the receiver
 * and return it so calls chain; take a snapshot with {@link clone} when a value must outlive the frame.
 *
 * The two public fields are the whole state, so a vector serializes as `{ x, y }` with no custom code.
 */
export class Vector2 {
  /** Horizontal component; positive is right. */
  x: number;
  /** Vertical component; positive is up, matching the engine's Y-up world. */
  y: number;

  constructor(x = 0, y = 0) {
    this.x = x;
    this.y = y;
  }

  /** A new vector at the origin. */
  static zero(): Vector2 {
    return new Vector2(0, 0);
  }

  /** A new vector with both components set to one, the identity for scaling. */
  static one(): Vector2 {
    return new Vector2(1, 1);
  }

  /** A new vector of the given length pointing along `radians`, measured counter-clockwise from +X. */
  static fromAngle(radians: number, length = 1): Vector2 {
    return new Vector2(Math.cos(radians) * length, Math.sin(radians) * length);
  }

  /** Overwrites both components. */
  set(x: number, y: number): this {
    this.x = x;
    this.y = y;
    return this;
  }

  /** Copies both components from `source`. */
  copyFrom(source: Vector2): this {
    this.x = source.x;
    this.y = source.y;
    return this;
  }

  /** An independent copy of this vector. */
  clone(): Vector2 {
    return new Vector2(this.x, this.y);
  }

  /** Adds `other` component-wise. */
  add(other: Vector2): this {
    this.x += other.x;
    this.y += other.y;
    return this;
  }

  /** Adds `other` scaled by `factor`, the shape most steering and velocity code needs. */
  addScaled(other: Vector2, factor: number): this {
    this.x += other.x * factor;
    this.y += other.y * factor;
    return this;
  }

  /** Subtracts `other` component-wise. */
  subtract(other: Vector2): this {
    this.x -= other.x;
    this.y -= other.y;
    return this;
  }

  /** Multiplies both components by `factor`. */
  scale(factor: number): this {
    this.x *= factor;
    this.y *= factor;
    return this;
  }

  /** Multiplies component-wise by `other`, used when composing non-uniform scales. */
  multiply(other: Vector2): this {
    this.x *= other.x;
    this.y *= other.y;
    return this;
  }

  /** Flips the vector to point the opposite way. */
  negate(): this {
    this.x = -this.x;
    this.y = -this.y;
    return this;
  }

  /** Rotates the vector counter-clockwise by `radians` about the origin. */
  rotate(radians: number): this {
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const rotatedX = this.x * cos - this.y * sin;
    this.y = this.x * sin + this.y * cos;
    this.x = rotatedX;
    return this;
  }

  /** Length of the vector. Prefer {@link lengthSquared} when only comparing magnitudes. */
  get length(): number {
    return Math.hypot(this.x, this.y);
  }

  /** Squared length, which avoids the square root when comparing distances. */
  get lengthSquared(): number {
    return this.x * this.x + this.y * this.y;
  }

  /** Angle from +X in radians, counter-clockwise. Zero for the zero vector. */
  get angle(): number {
    return Math.atan2(this.y, this.x);
  }

  /** Scales the vector to unit length. A zero length vector is left untouched. */
  normalize(): this {
    const currentLength = this.length;
    if (currentLength <= EPSILON) {
      return this;
    }
    return this.scale(1 / currentLength);
  }

  /** Caps the vector's length at `maxLength`, leaving shorter vectors untouched. */
  clampLength(maxLength: number): this {
    const currentLength = this.length;
    if (currentLength <= maxLength || currentLength <= EPSILON) {
      return this;
    }
    return this.scale(maxLength / currentLength);
  }

  /** Distance to `other`. */
  distanceTo(other: Vector2): number {
    return Math.hypot(other.x - this.x, other.y - this.y);
  }

  /** Squared distance to `other`, for cheap radius comparisons. */
  distanceSquaredTo(other: Vector2): number {
    const deltaX = other.x - this.x;
    const deltaY = other.y - this.y;
    return deltaX * deltaX + deltaY * deltaY;
  }

  /** Dot product with `other`. */
  dot(other: Vector2): number {
    return this.x * other.x + this.y * other.y;
  }

  /** Moves this vector a fraction `t` of the way towards `target`, with `t` clamped to zero to one. */
  lerpTo(target: Vector2, t: number): this {
    const clamped = clamp01(t);
    this.x += (target.x - this.x) * clamped;
    this.y += (target.y - this.y) * clamped;
    return this;
  }

  /** Reports whether both components match `other` to within `tolerance`. */
  equals(other: Vector2, tolerance: number = EPSILON): boolean {
    return approximately(this.x, other.x, tolerance) && approximately(this.y, other.y, tolerance);
  }
}
